import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiSearch, FiPrinter } from 'react-icons/fi';
import PersonSelectionModal from './PersonSelectionModal';
import PersonDetailsForm from './PersonDetailsForm';
import PrintReceiptForm from './PrintReceiptForm';
import IncomeCertificateDocument from './IncomeCertificateDocument';
import { db } from '../../users/pages/firebase';
import { ref, onValue, push, update, remove } from 'firebase/database';

const IncomeCertificate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { grampanchayatName, arthikVarshLabel } = location.state || {};

  const [view, setView] = useState('list'); // 'list' or 'form'
  const [searchTerm, setSearchTerm] = useState('');
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showPersonDetailsForm, setShowPersonDetailsForm] = useState(false);
  const [showPrintReceiptForm, setShowPrintReceiptForm] = useState(false);
  
  const [selectedPersonForEdit, setSelectedPersonForEdit] = useState(null);
  const [selectedCertForReport, setSelectedCertForReport] = useState(null);
  const [selectedCertForPrint, setSelectedCertForPrint] = useState(null);

  const [formData, setFormData] = useState({
    certificateNo: '',
    certificateDate: new Date().toISOString().split('T')[0],
    personName: '',
    financialYear: '',
    annualIncome: ''
  });

  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    const certRef = ref(db, '/Grampanchayat/Gramniddhi/Certificates and licenses/IncomeCertificate list');
    const unsubscribe = onValue(certRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const certList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setCertificates(certList);
      } else {
        setCertificates([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePersonSelect = (person) => {
    setFormData(prev => ({
      ...prev,
      personName: person.fullName,
      firstName: person.firstName || '',
      middleName: person.middleName || '',
      lastName: person.lastName || '',
      aadharNo: person.aadharNo || '',
      mobileNo: person.mobileNo || '', // If available from selection
    }));
  };

  const handleSave = () => {
    console.log("Saving Certificate:", formData);
    
    if (formData.id) {
        // Update existing
        const certRef = ref(db, `/Grampanchayat/Gramniddhi/Certificates and licenses/IncomeCertificate list/${formData.id}`);
        update(certRef, formData)
            .then(() => {
                alert("माहिती यशस्वीरित्या अपडेट झाली!");
                resetForm();
                setView('list');
            })
            .catch((error) => {
                console.error("Error updating document: ", error);
                alert("माहिती अपडेट करताना त्रुटी आली.");
            });
    } else {
        // Create new
        const certRef = ref(db, '/Grampanchayat/Gramniddhi/Certificates and licenses/IncomeCertificate list');
        push(certRef, formData)
            .then(() => {
                alert("माहिती यशस्वीरित्या साठवली!");
                resetForm();
                setView('list');
            })
            .catch((error) => {
                console.error("Error adding document: ", error);
                alert("माहिती साठवताना त्रुटी आली.");
            });
    }
  };

  const handlePersonDetailsSave = (updatedPersonData) => {
    if (!selectedPersonForEdit || !selectedPersonForEdit.id) return;

    const fullName = `${updatedPersonData.firstName} ${updatedPersonData.middleName} ${updatedPersonData.lastName}`.trim();
    
    const updatedData = {
        ...selectedPersonForEdit,
        ...updatedPersonData,
        personName: fullName
    };

    const certRef = ref(db, `/Grampanchayat/Gramniddhi/Certificates and licenses/IncomeCertificate list/${selectedPersonForEdit.id}`);
    update(certRef, updatedData)
        .then(() => {
            alert("व्यक्तीची माहिती यशस्वीरित्या अपडेट झाली!");
            setShowPersonDetailsForm(false);
            // Optional: Update local state if needed instantly, though onValue should handle it
        })
        .catch((error) => {
            console.error("Error updating person details: ", error);
            alert("त्रुटी आली: " + error.message);
        });
  };

  const handleReportSave = (reportData) => {
    if (!selectedCertForReport || !selectedCertForReport.id) return;

    const updatedData = {
        ...selectedCertForReport,
        ...reportData
    };

    const certRef = ref(db, `/Grampanchayat/Gramniddhi/Certificates and licenses/IncomeCertificate list/${selectedCertForReport.id}`);
    update(certRef, updatedData)
        .then(() => {
            alert("पावतीची माहिती यशस्वीरित्या साठवली!");
            setShowPrintReceiptForm(false);
        })
        .catch((error) => {
            console.error("Error updating report details: ", error);
            alert("त्रुटी आली: " + error.message);
        });
  };

  const handlePrintClick = (cert) => {
    setSelectedCertForPrint(cert);
    setTimeout(() => {
        window.print();
    }, 500);
  };

  const handleCancel = () => {
    setView('list');
    resetForm();
  };

  const resetForm = () => {
    setFormData({
        certificateNo: '',
        certificateDate: new Date().toISOString().split('T')[0],
        personName: '',
        financialYear: '',
        annualIncome: ''
    });
  };

  // Handlers for List Actions
  const handleEditClick = (cert) => {
    setSelectedPersonForEdit(cert);
    setShowPersonDetailsForm(true);
  };

  const handleReportClick = (cert) => {
    setSelectedCertForReport(cert);
    setShowPrintReceiptForm(true);
  };

  const filteredCertificates = certificates.filter(cert => 
    cert.personName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.certificateNo?.toString().includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 font-poppins">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area { visibility: visible; position: absolute; left: 0; top: 0; width: 100%; display: block !important; }
          .printable-area * { visibility: visible; }
        }
      `}</style>

      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-[#267c77] flex justify-between items-center">
            <div>
                 <h1 className="text-xl font-bold text-gray-800">
                    उत्पन्नाचा दाखला
                 </h1>
            </div>
            <div className="flex gap-2">
                 <button 
                   onClick={() => navigate('/asthapan/certificates', { state: location.state })}
                   className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                 >
                   मागे जा
                 </button>
                 {view === 'list' && (
                   <button 
                     onClick={() => setView('form')}
                     className="px-4 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition"
                   >
                     नवीन
                   </button>
                 )}
            </div>
        </div>
      </div>

      {view === 'list' ? (
        /* LIST VIEW */
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200 flex gap-4">
             <div className="relative flex-1">
                <input 
                  type="text" 
                  placeholder="दाखला क्र./व्यक्तीचे नाव" 
                  className="w-full px-4 py-2 border border-[#267c77]/30 rounded focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
             </div>
             <button className="px-6 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition">
                शोधणे
             </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#267c77] text-white">
                  <th className="p-3 border-r border-white/30 font-medium text-center">दाखला क्रमांक</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">देय दिनांक</th>
                  <th className="p-3 border-r border-white/30 font-medium">व्यक्तीचे नाव</th>
                  <th className="p-3 font-medium text-center">पर्याय</th>
                </tr>
              </thead>
              <tbody>
                {filteredCertificates.length === 0 ? (
                    <tr>
                        <td colSpan="4" className="p-4 text-center text-gray-500">
                            No records found
                        </td>
                    </tr>
                ) : (
                    filteredCertificates.map((cert, idx) => (
                        <tr key={idx} className="hover:bg-[#267c77]/10 border-b">
                            <td className="p-3 border-r text-center">{cert.certificateNo}</td>
                            <td className="p-3 border-r text-center">{cert.certificateDate}</td>
                            <td className="p-3 border-r">{cert.personName}</td>
                            <td className="p-3 text-center flex justify-center gap-2">
                                <button 
                                    onClick={() => handleEditClick(cert)}
                                    className="px-3 py-1 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] text-sm"
                                >
                                    बदल
                                </button>
                                <button 
                                    onClick={() => handleReportClick(cert)}
                                    className="px-3 py-1 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] text-sm"
                                >
                                    रिपोर्ट
                                </button>
                                <button 
                                    title="प्रिंट"
                                >
                                    <FiPrinter size={22} className="text-[#267c77]" />
                                </button>
                            </td>
                        </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

           {/* Pagination */}
           <div className="p-2 border-t bg-[#267c77] text-white flex justify-center items-center gap-2">
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">|&lt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&lt;&lt;</button>
                <span className="px-3 py-1 bg-white/20 rounded text-sm bg-[#1e6b66]">1</span>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;&gt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;|</button>
                <select className="ml-2 bg-white text-black text-sm p-1 rounded">
                    <option>5</option>
                    <option>10</option>
                    <option>20</option>
                </select>
           </div>
        </div>
      ) : (
        /* FORM VIEW */
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 border-t-4 border-[#267c77]">
            <div className="space-y-6">
                
                {/* Certificate No */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <label className="text-gray-700 font-medium md:col-span-1">दाखला क्रमांक :</label>
                    <div className="md:col-span-3">
                         <input 
                            type="text" 
                            name="certificateNo"
                            value={formData.certificateNo}
                            onChange={handleInputChange}
                            placeholder="दाखला क्रमांक"
                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#267c77] py-1.5 bg-gray-100"
                         />
                    </div>
                </div>

                {/* Date */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <label className="text-gray-700 font-medium md:col-span-1">देय दिनांक :</label>
                    <div className="md:col-span-3 relative">
                         <input 
                            type="date"
                            name="certificateDate"
                            value={formData.certificateDate}
                            onChange={handleInputChange} 
                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#267c77] py-1.5"
                         />
                    </div>
                </div>

                 {/* Person Name */}
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <label className="text-gray-700 font-medium md:col-span-1">व्यक्तीचे नाव :</label>
                    <div className="md:col-span-3 flex gap-0">
                         <input 
                            type="text"
                            name="personName"
                            value={formData.personName}
                            onChange={handleInputChange} 
                            placeholder="व्यक्तीचे नाव"
                            className="flex-1 p-2 border border-gray-300 rounded-l focus:ring-2 focus:ring-[#267c77] py-1.5 bg-gray-100"
                         />
                         <button 
                            onClick={() => setShowPersonModal(true)}
                            className="px-4 bg-[#267c77] text-white rounded-r hover:bg-[#1e6b66]"
                        >
                            ...
                         </button>
                    </div>
                </div>

                 {/* Financial Year */}
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <label className="text-gray-700 font-medium md:col-span-1">आर्थिक वर्ष :</label>
                    <div className="md:col-span-3">
                         <input 
                            type="text" 
                            name="financialYear"
                            value={formData.financialYear}
                            onChange={handleInputChange}
                            placeholder="आर्थिक वर्ष भरा."
                            className="w-full p-2 border border-[#267c77]/30 rounded focus:ring-2 focus:ring-[#267c77] py-1.5"
                         />
                    </div>
                </div>

                {/* Annual Income */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <label className="text-gray-700 font-medium md:col-span-1">अंदाजे वार्षिक उत्पन्न :</label>
                    <div className="md:col-span-3">
                         <input 
                            type="text" 
                            name="annualIncome"
                            value={formData.annualIncome}
                            onChange={handleInputChange}
                            placeholder="अंदाजे वार्षिक उत्पन्न भरा."
                            className="w-full p-2 border border-[#267c77]/30 rounded focus:ring-2 focus:ring-[#267c77] py-1.5"
                         />
                    </div>
                </div>

            </div>

             {/* Footer Buttons */}
             <div className="mt-8 flex justify-center gap-4 py-4 bg-[#267c77] rounded-b-lg -mx-6 -mb-6">
                <button 
                  onClick={handleSave}
                  className="px-6 py-2 bg-[#267c77] hover:bg-[#1e6b66] text-white rounded transition font-medium"
                >
                    साठविणे
                </button>
                <button 
                  onClick={handleCancel}
                  className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition font-medium"
                >
                    रद्द करणे
                </button>
             </div>
        </div>
      )}

      {/* Person Selection Modal */}
      <PersonSelectionModal 
        isOpen={showPersonModal}
        onClose={() => setShowPersonModal(false)}
        onSelect={handlePersonSelect}
      />
      
      {/* Edit Person Details Form */}
      <PersonDetailsForm 
        isOpen={showPersonDetailsForm}
        onClose={() => setShowPersonDetailsForm(false)}
        personData={selectedPersonForEdit}
        onSave={handlePersonDetailsSave}
      />

      <PrintReceiptForm 
        isOpen={showPrintReceiptForm}
        onClose={() => setShowPrintReceiptForm(false)}
        certificateData={selectedCertForReport}
        defaultRemark="उत्पन्नाचा दाखला प्रत छपाईचे रोख जमा"
        onSave={handleReportSave}
      />

      <div className="printable-area hidden">
        <IncomeCertificateDocument data={selectedCertForPrint} />
      </div>
    </div>
  );
};

export default IncomeCertificate;
